import type { Language } from '@offer-catalog/services/i18n'
import type { FilterGroup } from '@/contexts/products/ProductsContext'
import {
  VpAccordionDivider,
  VpAccordionItem,
  VpAccordionItemHeader,
  VpAccordionItemHeaderIcon,
  VpAccordionItemHeaderLabel,
  VpAccordionItemPanel,
  VpCheckbox,
  VpIcon,
  VpLink,
  VpLoader,
} from '@vtmn-play/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { useProductsContext } from '@/contexts/products/useProductsContext'
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '@/services/analytics'

export interface FiltersBarGroupProps {
  group: FilterGroup
}

export function FiltersBarGroup({ group }: Readonly<FiltersBarGroupProps>) {
  const { i18n, t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    loadFacetGroupLabels = async () => {},
    loadingFacetGroups = new Set<string>(),
    facetQueryKey,
    isFetchingFacets = false,
    viewMode,
    sortCriteria,
    sortOrder,
  } = useProductsContext()

  const [isExpanded, setIsExpanded] = useState(false)

  const currentLanguage = i18n.language.split('-')[0] as Language
  const isLoading = loadingFacetGroups.has(group.id)
  const itemsToShow = isExpanded ? group.filters : group.filters.slice(0, 10)

  const selectedFilterIds = searchParams.getAll(group.id)
  const selectedFiltersCount = group.filters.filter(filter => selectedFilterIds.includes(filter.id)).length
  const selectedFiltersLabel = selectedFiltersCount > 0 ? ` (${selectedFiltersCount})` : ''

  useEffect(() => {
    if (isExpanded && !isFetchingFacets)
      void loadFacetGroupLabels(group.id)
  }, [facetQueryKey, group.id, isExpanded, isFetchingFacets, loadFacetGroupLabels])

  const onToggleFilter = (filterId: string) => {
    const wasSelected = selectedFilterIds.includes(filterId)
    const selectedFiltersInGroup = selectedFiltersCount

    trackAnalyticsEvent(ANALYTICS_EVENTS.PRODUCT_FILTER_TOGGLED, {
      filter_group_id: group.id,
      filter_group_label: t(`products-page.filters.groups.${group.id}`),
      filter_value_id: filterId,
      action: wasSelected ? 'remove' : 'add',
      selected_filters_in_group_count: wasSelected
        ? Math.max(0, selectedFiltersInGroup - 1)
        : selectedFiltersInGroup + 1,
      new_product_only: searchParams.has('is_new'),
      view_mode: viewMode,
      sort_criteria: sortCriteria,
      sort_order: sortOrder,
    })

    if (!wasSelected) {
      trackAnalyticsEvent(ANALYTICS_EVENTS.PRODUCT_FILTER_USED, {
        filter_group_id: group.id,
        filter_group_label: t(`products-page.filters.groups.${group.id}`),
        filter_value_id: filterId,
        filter_value_label: group.filters.find(filter => filter.id === filterId)?.label[currentLanguage],
        selected_filters_count: selectedFiltersInGroup + 1,
        new_product_only: searchParams.has('is_new'),
        view_mode: viewMode,
        sort_criteria: sortCriteria,
        sort_order: sortOrder,
      })
    }

    if (wasSelected) {
      searchParams.delete(group.id)
      selectedFilterIds
        .filter(selectedFilterId => selectedFilterId !== filterId)
        .forEach(selectedFilterId => searchParams.append(group.id, selectedFilterId))
    }
    else {
      searchParams.append(group.id, filterId)
    }
    setSearchParams(searchParams)
  }

  const onExpand = async () => {
    if (isLoading)
      return

    await loadFacetGroupLabels(group.id)
    setIsExpanded(true)
  }

  return (
    <>
      <VpAccordionItem value={group.id}>
        <VpAccordionItemHeader>
          <VpAccordionItemHeaderLabel>
            {t(`products-page.filters.groups.${group.id}`)}
            <span>{selectedFiltersLabel}</span>
          </VpAccordionItemHeaderLabel>
          <VpAccordionItemHeaderIcon />
        </VpAccordionItemHeader>
        <VpAccordionItemPanel>
          {itemsToShow.map(filter => (
            <VpCheckbox
              key={filter.id}
              checked={selectedFilterIds.includes(filter.id)}
              disabled={isFetchingFacets}
              onChange={() => onToggleFilter(filter.id)}
            >
              {filter.label[currentLanguage]}
              {' '}
              <span>{filter.count}</span>
            </VpCheckbox>
          ))}
          {group.filters.length > 10 && !isExpanded && (
            <VpLink
              endSlot={isLoading ? <VpLoader data-testid="facet-expansion-loader" size="small" /> : <VpIcon name="chevron-right" />}
              size="small"
              aria-busy={isLoading}
              aria-disabled={isLoading}
              onClick={onExpand}
            >
              {t('products-page.filters.show-all')}
            </VpLink>
          )}
        </VpAccordionItemPanel>
      </VpAccordionItem>
      <VpAccordionDivider />
    </>
  )
}
